import React, { useState } from "react";
import { Brain, ChevronDown, ChevronUp, Coins, Eye, Globe, Heart, MapPin, MessageSquare, Shield, Sword, User, Zap } from "lucide-react";
import { colourScheme } from "@/utils/styling/colours";
import { AttributeDTO, DnD5eNPCDTO, SkillDTO } from "types";
import * as Styled from "./NPCStats.styled";
function CompactList({
  items,
  title,
  icon
}: {
  items: string[];
  title: string;
  icon: React.ReactNode;
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  return <Styled.CompactListContainer data-sentry-element="unknown" data-sentry-component="CompactList" data-sentry-source-file="index.tsx">
            <Styled.CompactListButton onClick={() => setIsExpanded(!isExpanded)} data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                <Styled.TitleContainer data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                    {icon}
                    <Styled.TitleText data-sentry-element="unknown" data-sentry-source-file="index.tsx">{title}</Styled.TitleText>
                </Styled.TitleContainer>
                {isExpanded ? <ChevronUp size={16} color={colourScheme.secondaryLight} /> : <ChevronDown size={16} color={colourScheme.secondaryLight} />}
            </Styled.CompactListButton>
            {isExpanded && <Styled.List>
                    {items.map((item, index) => <Styled.ListItem key={index}>{item}</Styled.ListItem>)}
                </Styled.List>}
        </Styled.CompactListContainer>;
}
function StatBar({
  name,
  value,
  modifier,
  icon
}: {
  name: string;
  value: string;
  modifier: string;
  icon: React.ReactNode;
}) {
  const abbreviation = name.substring(0, 3).toUpperCase();
  return <Styled.StatBarContainer data-sentry-element="unknown" data-sentry-component="StatBar" data-sentry-source-file="index.tsx">
            <Styled.StatBarIconLarge data-sentry-element="unknown" data-sentry-source-file="index.tsx">{icon}</Styled.StatBarIconLarge>
            <Styled.StatBarName data-sentry-element="unknown" data-sentry-source-file="index.tsx">{abbreviation}</Styled.StatBarName>
            <Styled.StatBarValueContainer data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                <Styled.StatBarValue data-sentry-element="unknown" data-sentry-source-file="index.tsx">{value}</Styled.StatBarValue>
                <Styled.StatBarModifier data-sentry-element="unknown" data-sentry-source-file="index.tsx">({modifier})</Styled.StatBarModifier>
            </Styled.StatBarValueContainer>
        </Styled.StatBarContainer>;
}
function StatGrid({
  items,
  title
}: {
  items: AttributeDTO[];
  title: string;
}) {
  const getIcon = (name: string) => {
    const iconProps = {
      size: 32,
      // Adjusted icon size to match 'text-3xl'
      color: colourScheme.secondaryLight
    };
    const abbreviation = name.substring(0, 3).toUpperCase();
    switch (abbreviation) {
      case "STR":
        return <Sword {...iconProps} />;
      case "DEX":
        return <Zap {...iconProps} />;
      case "CON":
        return <Shield {...iconProps} />;
      case "INT":
        return <Brain {...iconProps} />;
      case "WIS":
        return <Eye {...iconProps} />;
      case "CHA":
        return <Heart {...iconProps} />;
      default:
        return null;
    }
  };
  return <Styled.StatGridContainer data-sentry-element="unknown" data-sentry-component="StatGrid" data-sentry-source-file="index.tsx">
            <Styled.StatGridTitle data-sentry-element="unknown" data-sentry-source-file="index.tsx">{title}</Styled.StatGridTitle>
            <Styled.StatGridGrid data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                {items.map(item => <StatBar key={item.name} name={item.name} value={item.value} modifier={item.modifier} icon={getIcon(item.name)} />)}
            </Styled.StatGridGrid>
        </Styled.StatGridContainer>;
}
function SkillList({
  skills
}: {
  skills: SkillDTO[];
}) {
  return <Styled.SkillListGrid data-sentry-element="unknown" data-sentry-component="SkillList" data-sentry-source-file="index.tsx">
            {skills.map(skill => <Styled.SkillItem key={skill.name}>
                    <Styled.SkillName>{skill.name}</Styled.SkillName>
                    <Styled.SkillValue>{skill.bonus}</Styled.SkillValue>
                </Styled.SkillItem>)}
        </Styled.SkillListGrid>;
}
export default function Component({
  stats
}: {
  stats: DnD5eNPCDTO;
}) {
  const [activeTab, setActiveTab] = useState("basic");
  return <Styled.Card data-sentry-element="unknown" data-sentry-component="Component" data-sentry-source-file="index.tsx">
            <Styled.CardContent data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                <Styled.HeaderContainer data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                    <Styled.HeaderTitle data-sentry-element="unknown" data-sentry-source-file="index.tsx">{stats.name}</Styled.HeaderTitle>
                    <Styled.HeaderLevel data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                        Level {stats.level || "?"}
                    </Styled.HeaderLevel>
                    <Styled.HeaderSubtitle data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                        {stats.npcClass} - {stats.race} | {stats.background}
                    </Styled.HeaderSubtitle>
                </Styled.HeaderContainer>
                <Styled.Tabs value={activeTab} onValueChange={setActiveTab} data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                    <Styled.TabsList data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                        {["basic", "personality", "roleplay", "combat", "equipment"].map(tab => <Styled.TabsTrigger key={tab} value={tab} className={activeTab === tab ? "active" : ""}>
                                {tab.charAt(0).toUpperCase() + tab.slice(1)}
                            </Styled.TabsTrigger>)}
                    </Styled.TabsList>
                    <Styled.ContentContainer data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                        {/* Basic Tab Content */}
                        <Styled.TabsContent value="basic" className={activeTab === "basic" ? "visible" : "hidden"} data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                            <Styled.GridTwoColumns data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                                <Styled.InfoCard data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                                    <Styled.InfoTitle data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                                        Occupation
                                    </Styled.InfoTitle>
                                    <Styled.InfoText data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                                        {stats.occupation}
                                    </Styled.InfoText>
                                </Styled.InfoCard>
                                <Styled.InfoCard data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                                    <Styled.InfoTitle data-sentry-element="unknown" data-sentry-source-file="index.tsx">Faction</Styled.InfoTitle>
                                    <Styled.InfoText data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                                        {stats.factionAffiliations}
                                    </Styled.InfoText>
                                </Styled.InfoCard>
                            </Styled.GridTwoColumns>
                            <Styled.InfoCard data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                                <Styled.InfoTitle data-sentry-element="unknown" data-sentry-source-file="index.tsx">Environment</Styled.InfoTitle>
                                <div style={{
                display: "flex",
                alignItems: "center"
              }}>
                                    <MapPin size={16} color={colourScheme.secondaryLight} style={{
                  marginRight: "0.5rem"
                }} data-sentry-element="MapPin" data-sentry-source-file="index.tsx" />
                                    <Styled.InfoText data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                                        {stats.environment}
                                    </Styled.InfoText>
                                </div>
                            </Styled.InfoCard>
                            <Styled.InfoCard data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                                <Styled.InfoTitle data-sentry-element="unknown" data-sentry-source-file="index.tsx">Languages</Styled.InfoTitle>
                                <div style={{
                display: "flex",
                alignItems: "center"
              }}>
                                    <Globe size={16} color={colourScheme.secondaryLight} style={{
                  marginRight: "0.5rem"
                }} data-sentry-element="Globe" data-sentry-source-file="index.tsx" />
                                    <Styled.InfoText data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                                        {stats.languages.join(", ")}
                                    </Styled.InfoText>
                                </div>
                            </Styled.InfoCard>
                            <StatGrid items={stats.attributes} title="Attributes" data-sentry-element="StatGrid" data-sentry-source-file="index.tsx" />
                            <div>
                                <Styled.SectionTitle data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                                    Skills
                                </Styled.SectionTitle>
                                <SkillList skills={stats.skills} data-sentry-element="SkillList" data-sentry-source-file="index.tsx" />
                            </div>
                        </Styled.TabsContent>

                        {/* Personality Tab Content */}
                        <Styled.TabsContent value="personality" className={activeTab === "personality" ? "visible" : "hidden"} data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                            <Styled.GridThreeColumns data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                                <CompactList items={stats.personalityTraits} title="Traits" key="traits" icon={<Heart size={16} color={colourScheme.secondaryLight} />} data-sentry-element="CompactList" data-sentry-source-file="index.tsx" />
                                <CompactList items={stats.flaws} title="Flaws" key="flaws" icon={<ChevronDown size={16} color={colourScheme.secondaryLight} />} data-sentry-element="CompactList" data-sentry-source-file="index.tsx" />
                                <CompactList items={stats.fears} title="Fears" key="fears" icon={<Eye size={16} color={colourScheme.secondaryLight} />} data-sentry-element="CompactList" data-sentry-source-file="index.tsx" />
                            </Styled.GridThreeColumns>
                            <CompactList items={stats.wantsNeeds} title="Wants & Needs" key="wantsNeeds" icon={<Zap size={16} color={colourScheme.secondaryLight} />} data-sentry-element="CompactList" data-sentry-source-file="index.tsx" />
                            <Styled.InfoCard data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                                <Styled.InfoTitle data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                                    Major Secret
                                </Styled.InfoTitle>
                                <Styled.InfoText data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                                    {stats.majorSecret}
                                </Styled.InfoText>
                            </Styled.InfoCard>
                            <Styled.InfoCard data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                                <Styled.InfoTitle data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                                    Minor Secrets
                                </Styled.InfoTitle>
                                <Styled.InfoText data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                                    {stats.minorSecrets}
                                </Styled.InfoText>
                            </Styled.InfoCard>
                        </Styled.TabsContent>

                        {/* Roleplay Tab Content */}
                        <Styled.TabsContent value="roleplay" className={activeTab === "roleplay" ? "visible" : "hidden"} data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                            <Styled.InfoCard data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                                <Styled.InfoTitle data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                                    Dialogue Patterns
                                </Styled.InfoTitle>
                                <Styled.ListDisc data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                                    {stats.dialoguePatterns.map((pattern, index) => <Styled.ListItem key={index}>
                                                {pattern}
                                            </Styled.ListItem>)}
                                </Styled.ListDisc>
                            </Styled.InfoCard>
                            <Styled.InfoCard data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                                <Styled.InfoTitle data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                                    Body Language
                                </Styled.InfoTitle>
                                <div style={{
                display: "flex",
                alignItems: "center"
              }}>
                                    <User size={16} color={colourScheme.secondaryLight} style={{
                  marginRight: "0.5rem"
                }} data-sentry-element="User" data-sentry-source-file="index.tsx" />
                                    <Styled.InfoText data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                                        {stats.bodyLanguage}
                                    </Styled.InfoText>
                                </div>
                            </Styled.InfoCard>
                            <Styled.InfoCard data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                                <Styled.InfoTitle data-sentry-element="unknown" data-sentry-source-file="index.tsx">Voice</Styled.InfoTitle>
                                <div style={{
                display: "flex",
                alignItems: "center"
              }}>
                                    <MessageSquare size={16} color={colourScheme.secondaryLight} style={{
                  marginRight: "0.5rem"
                }} data-sentry-element="MessageSquare" data-sentry-source-file="index.tsx" />
                                    <Styled.InfoText data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                                        {stats.voice}
                                    </Styled.InfoText>
                                </div>
                            </Styled.InfoCard>
                            <Styled.InfoCard data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                                <Styled.InfoTitle data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                                    Behavior Patterns
                                </Styled.InfoTitle>
                                <Styled.ListDisc data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                                    {stats.behaviorPatterns.map((pattern, index) => <Styled.ListItem key={index}>
                                                {pattern}
                                            </Styled.ListItem>)}
                                </Styled.ListDisc>
                            </Styled.InfoCard>
                            <Styled.InfoCard data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                                <Styled.InfoTitle data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                                    Interaction Strategy
                                </Styled.InfoTitle>
                                <Styled.InfoText data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                                    {stats.interactionStrategy}
                                </Styled.InfoText>
                            </Styled.InfoCard>
                        </Styled.TabsContent>

                        {/* Combat Tab Content */}
                        <Styled.TabsContent value="combat" className={activeTab === "combat" ? "visible" : "hidden"} data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                            <Styled.GridThreeColumns data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                                <Styled.InfoCard data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                                    <Styled.InfoTitle data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                                        Armor Class
                                    </Styled.InfoTitle>
                                    <Styled.InfoText style={{
                  fontSize: "1.25rem",
                  fontWeight: "bold"
                }} data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                                        {stats.armorClass}
                                    </Styled.InfoText>
                                </Styled.InfoCard>
                                <Styled.InfoCard data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                                    <Styled.InfoTitle data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                                        Hit Points
                                    </Styled.InfoTitle>
                                    <Styled.InfoText style={{
                  fontSize: "1.25rem",
                  fontWeight: "bold"
                }} data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                                        {stats.hitPoints}
                                    </Styled.InfoText>
                                </Styled.InfoCard>
                                <Styled.InfoCard data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                                    <Styled.InfoTitle data-sentry-element="unknown" data-sentry-source-file="index.tsx">Speed</Styled.InfoTitle>
                                    <Styled.InfoText style={{
                  fontSize: "1.25rem",
                  fontWeight: "bold"
                }} data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                                        {stats.speed}
                                    </Styled.InfoText>
                                </Styled.InfoCard>
                            </Styled.GridThreeColumns>
                            <Styled.InfoCard data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                                <Styled.SectionTitle data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                                    Actions & Attacks
                                </Styled.SectionTitle>
                                <div>
                                    {stats.actions && stats.actions.map((action: {
                  name: string;
                  description: string;
                }, index: React.Key | null | undefined) => <Styled.ActionCard key={index}>
                                                    <Styled.ActionName>
                                                        {action.name}
                                                    </Styled.ActionName>
                                                    <Styled.ActionDescription>
                                                        {action.description}
                                                    </Styled.ActionDescription>
                                                </Styled.ActionCard>)}
                                </div>
                            </Styled.InfoCard>
                            <Styled.InfoCard data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                                <Styled.InfoTitle data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                                    Combat Strategy
                                </Styled.InfoTitle>
                                <Styled.InfoText data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                                    {stats.combatStrategy}
                                </Styled.InfoText>
                            </Styled.InfoCard>
                        </Styled.TabsContent>

                        {/* Equipment Tab Content */}
                        <Styled.TabsContent value="equipment" className={activeTab === "equipment" ? "visible" : "hidden"} data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                            <div>
                                {stats.equipment.map((item, index) => <Styled.EquipmentCard key={index}>
                                        <div style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center"
                }}>
                                            <Styled.EquipmentTitle>
                                                {item.name}
                                            </Styled.EquipmentTitle>
                                            <Styled.Badge>
                                                {item.type}
                                            </Styled.Badge>
                                        </div>
                                        <Styled.EquipmentDescription>
                                            {item.description}
                                        </Styled.EquipmentDescription>
                                        <Styled.EquipmentValueContainer>
                                            <Coins size={12} color={colourScheme.alert} />
                                            <Styled.EquipmentValueText>
                                                {item.value}
                                            </Styled.EquipmentValueText>
                                        </Styled.EquipmentValueContainer>
                                    </Styled.EquipmentCard>)}
                            </div>
                            {["Services", "Rewards"].map(section => <div key={section} style={{
              marginTop: "1rem"
            }}>
                                    <Styled.SectionTitle>
                                        {section}
                                    </Styled.SectionTitle>
                                    <div style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "0.25rem"
              }}>
                                        {stats[section.toLowerCase() as keyof DnD5eNPCDTO].map((item: string, index: number) => <Styled.Badge key={index}>
                                                {item}
                                            </Styled.Badge>)}
                                    </div>
                                </div>)}
                        </Styled.TabsContent>
                    </Styled.ContentContainer>
                </Styled.Tabs>
            </Styled.CardContent>
        </Styled.Card>;
}